/*
 * Authors: mariushirjanu
 * Created: 28/03/2024
 */
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { IPaymentMatchTransaction, PaymentMatcherDataService } from './payment-matcher-data.service';
import { ICompanyCountry } from '@type_definitions/datatypes';
import { PaymentMatcherService } from './payment-matcher.service';
import { FormattingService } from '@app/shared/services/formatting.service';

@Component({
  selector: 'payment-transaction-details',
  template: `
    <h4 class="form-title">Isabel Transaction Data</h4>
    <formly [fields]="fields" [model]="model" [form]="form"></formly>
  `
})
export class PaymentTransactionDetailsComponent implements OnInit, OnChanges {
  @Input() public model:IPaymentMatchTransaction;
  @Input() public issuedTo:ICompanyCountry[];
  @Output() public modelChange:EventEmitter<IPaymentMatchTransaction> = new EventEmitter<IPaymentMatchTransaction>();
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];

  private saveCommentLinkField:IExtraFieldConfigurationObject;
  private backupComments:string;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private paymentMatcherService:PaymentMatcherService,
    private paymentMatcherDataService:PaymentMatcherDataService,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    /***/
  }

  public ngOnInit():void {
    this.fields = this.createFields();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.model) {
      this.model = changes.model.currentValue;
      this.backupComments = this.model.comments;
    }

    if (changes.issuedTo) {
      this.issuedTo = changes.issuedTo.currentValue;
    }
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-md-5 col-xxl-4';
    const valueClass:string = 'col-md-7 col-xxl-8';
    const statusNewOrProposed:boolean = this.paymentMatcherService.isStatusNewOrProposed(this.model.processingStatus);

    const fields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('processingStatus', 'Status')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('transactionId', 'Transaction ID')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('endToEndBankId', 'E2E Bank ID')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('accountName', 'Account owner')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('accountNumber', 'Account number')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('amount', 'Amount')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatAmount)
        .build(),

      this.formlyService.aValue('currency', 'Currency')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('transactionDate', 'Transaction date')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatDate)
        .build(),

      this.formlyService.aValue('message', 'Message')
        .withLabelClass('col-md-12')
        .withValueClass('col-md-12')
        .withoutEditable()
        .build(),

      this.formlyService.aValue('additionalInfo', 'Full message')
        .withLabelClass('col-md-12')
        .withValueClass('col-md-12')
        .withoutEditable()
        .build(),

      this.formlyService.aTextarea('comments', 'GA.GENERAL.COMMENTS')
        .withLabelClass('col-md-12')
        .withValueClass('col-md-12')
        .withoutEditable()
        .onChange((comments:string) => {
          this.model.comments = comments;
        })
        .build()
    ];

    if(!statusNewOrProposed){
      fields.push(
        this.saveCommentLinkField = this.formlyService.aLink('Save comments')
          .withLabelClass('d-none')
          .withExtraOptions({
            templateOptions: {
              linkClass: 'btn btn-success'
            }
          })
          .onClick(this.onSaveComments)
          .withHideExpression(() => this.backupComments === this.model.comments)
          .build()
      );
    }

    return fields;
  }

  private onSaveComments = ():void => {
    this.saveCommentLinkField.templateOptions.busy = true;
    this.paymentMatcherDataService.saveComment(this.model.transactionId, this.model?.comments)
      .then(() => {
        this.backupComments = this.model.comments;
        this.changeDetectorRef.detectChanges();
      })
      .finally(() => {
        this.saveCommentLinkField.templateOptions.busy = false;
      });
  };
}